import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  createTicket,
  searchTicketAffected,
  ticketCreateFormQuery,
  type AffectedKind,
  type TicketCreateAsk,
  type TicketCreateFormPage,
  type TicketFormField,
  type TicketFormOption,
} from '../api/easeedesk'
import { createFormMatrixQuery, IMPACT_FIELD, matrixPriority, priorityName, URGENCY_FIELD } from '../api/priorityMatrix'
import { SelectPicker, type SelectPickerOption } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { PriorityMatrixFields } from './PriorityMatrixFields'
import { Field, initial, isEmpty, merge, type Values } from './TicketActionForm'
import './TicketActionForm.css'

/**
 * FORMULARUL DE TICHET NOU - fereastra din spatele butonului "New ticket".
 *
 * In portal: `#createTicketModal` din `tickets/index.php`, umplut la deschidere
 * de `TicketFormBuilder.loadNewTicketForm()` (`easeedesk.js`), care cerea
 * descrierea campurilor de la `easeedesk/createTicketForm` si le desena. Aici
 * descrierea vine de la `GET tickets/create-form` (`TicketCreateForm.php`) si
 * campurile se deseneaza cu ACELASI `Field` ca formularele filei "Change":
 * formularul e configurabil in setari, si un camp adaugat de client trebuie sa
 * apara si aici fara ca cineva sa lege ceva.
 *
 * ===========================================================================
 * CASCADELE
 * ===========================================================================
 *
 * Portalul reincarca tot formularul la schimbarea TIPULUI (`newtype`), si cate
 * un sertar la fiecare din celelalte alegeri (`$.fn.reloadFields`): categoria
 * umple subcategoriile, echipa umple operatorii, firma umple punctele de lucru
 * si contactele. Aici toate cinci se cer inapoi de la server, in adresa
 * formularului (`ask`), si ce a completat omul intre timp ramane - `merge()`.
 * Doua exceptii, ca in portal: la schimbarea tipului prioritatea se pune pe
 * cea implicita a tipului nou (`typeDefaultPriority`), iar subcategoria si
 * operatia se golesc cand se schimba parintele lor.
 *
 * ===========================================================================
 * AFECTATUL
 * ===========================================================================
 *
 * `affected_select` e, in portal, un sertar de FEL (User / Equipment) si un
 * sertar care CAUTA pe masura ce omul scrie (`ajaxSelectPicker` pe
 * `get_field_options`), fiindca o firma poate avea mii de statii. La fel aici:
 * felul schimba ce se cauta, textul pleaca la `create-form/affected`, iar
 * echipamentele si elementele de configurare se aleg mai multe deodata
 * (`ticket_affected[]`), omul unul singur.
 *
 * ===========================================================================
 * FISIERELE
 * ===========================================================================
 *
 * Portalul le trimitea in acelasi POST (`ticket_attachments[]`). Aici
 * fisierele pleaca DUPA ce tichetul exista, pe capatul lor
 * (`POST tickets/{id}/attachments`), fiindca acela cere un numar de tichet.
 * Un camp de fisier OBLIGATORIU nu se poate deci implini la nastere, si
 * serverul refuza cu 501 si cu un mesaj - mesajul se vede in formular, nu se
 * inghite.
 *
 * ===========================================================================
 * DUPA SALVARE
 * ===========================================================================
 *
 * Ca in portal (`SubmitTicketForm.create` -> `preSubmitForm`): "Ticket
 * created.", fereastra se inchide, lista se redeseneaza pe loc. In plus,
 * numarul tichetului ajunge la cine a deschis fereastra (`onDone`), ca lista
 * sa-l poata evidentia - portalul nu-l primea, fiindca `createTicket`
 * raspundea cu un corp gol.
 *
 * ===========================================================================
 * MATRICEA URGENTA x IMPACT (CEC L25)
 * ===========================================================================
 *
 * Cand furnizorul a aprins-o in setari, formularul mai are doua liste -
 * urgenta si impactul - si prioritatea se CALCULEAZA din ele. Cine n-are
 * dreptul de suprascriere nu mai vede lista de prioritati: vede prioritatea
 * calculata. Cine il are, vede lista, pusa pe cea calculata, si o poate
 * schimba (serverul tine minte ca a fost suprascrisa). Impactul se SUGEREAZA
 * din CI-urile afectate alese, ca buton. Matricea stinsa: nimic nu se schimba.
 */

export function TicketCreateForm({
  onDone,
  onCancel,
}: {
  /** Tichetul s-a scris; numarul lui. */
  onDone(ticket: number): void
  onCancel(): void
}) {
  const [ask, setAsk] = useState<TicketCreateAsk>({})
  const { data, error, isFetching } = useQuery(ticketCreateFormQuery(ask))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  if (data.blocked) {
    return (
      <div className="alert alert-info" role="alert">
        <i className="fas fa-info-circle" aria-hidden="true"></i> {data.blocked.message}
      </div>
    )
  }

  return <Fields page={data} ask={ask} busy={isFetching} onAsk={setAsk} onDone={onDone} onCancel={onCancel} />
}

/** Alegerile care schimba o lista, si cheia din adresa pe care o schimba. */
const RELOADS: Record<string, keyof TicketCreateAsk> = {
  ticket_type: 'type',
  ticket_status: 'status',
  ticket_company: 'company',
  ticket_category: 'category',
  ticket_subcategory: 'subcategory',
  assigned_group: 'group',
}

/** Copiii care se golesc cand li se schimba parintele (`$.fn.reloadFields`). */
const CHILDREN: Record<string, string[]> = {
  ticket_category: ['ticket_subcategory', 'ticket_operation'],
  ticket_subcategory: ['ticket_operation'],
  assigned_group: ['assignee'],
  ticket_company: ['ticket_office', 'ticket_affected', 'ticket_affected_user', 'ticket_notifiers'],
}

function Fields({
  page,
  ask,
  busy,
  onAsk,
  onDone,
  onCancel,
}: {
  page: TicketCreateFormPage
  ask: TicketCreateAsk
  busy: boolean
  onAsk(next: TicketCreateAsk): void
  onDone(ticket: number): void
  onCancel(): void
}) {
  const client = useQueryClient()
  const [values, setValues] = useState<Values>(() => initial(page.fields))
  const [files, setFiles] = useState<Record<string, File[]>>({})
  const [sending, setSending] = useState(false)
  /* Paza de dublu-clic: `sending` e o stare, deci al doilea clic din acelasi
     moment inca il vede `false`; referinta se schimba pe loc. */
  const inFlight = useRef(false)
  /* Dupa prima incercare de trimitere, campurile obligatorii goale se arata in
     formular (eticheta rosie si randul de sub camp), nu doar intr-un toast. */
  const [tried, setTried] = useState(false)
  /** Refuzul serverului, aratat in formular - `.form-errors` din portal. */
  const [refused, setRefused] = useState<string | null>(null)
  const lastType = useRef(page.type)

  /* Matricea Urgenta x Impact. CI-urile alese ca afectati cer sugestia de impact. */
  const [urgency, setUrgency] = useState(0)
  const [impact, setImpact] = useState(0)
  const chosenCis =
    values.affected_type === 'ci' && Array.isArray(values.ticket_affected) ? values.ticket_affected.map(Number).filter((n) => n > 0) : []
  const matrixQuery = useQuery({ ...createFormMatrixQuery(chosenCis), placeholderData: (previous) => previous })
  const matrix = matrixQuery.data?.enabled === true ? matrixQuery.data : undefined
  const computed = matrixPriority(matrix, urgency, impact)

  useEffect(() => {
    setValues((was) => {
      const next = merge(was, page.fields)
      if (page.type !== lastType.current) {
        /* `newtype`: prioritatea tipului nou bate ce era ales (`typeDefaultPriority`). */
        lastType.current = page.type
        for (const field of page.fields) {
          if (field.key === 'ticket_priority' || field.key === 'ticket_status') next[field.key] = field.value ?? ''
        }
      }
      return next
    })
  }, [page.fields, page.type])

  useEffect(() => {
    /* Prioritatea calculata se pune pe lista, ca omul s-o vada aleasa; cine are
       dreptul o poate schimba dupa. DUPA efectul de deasupra: la schimbarea
       tipului, cea implicita a tipului nu are voie sa bata matricea. */
    if (computed === null) return
    setValues((was) => (was.ticket_priority === String(computed) ? was : { ...was, ticket_priority: String(computed) }))
  }, [computed, page.fields, page.type])

  function set(key: string, next: string | string[]): void {
    setValues((was) => {
      const out = { ...was, [key]: next }
      for (const child of CHILDREN[key] ?? []) {
        if (child in out) out[child] = Array.isArray(out[child]) ? [] : ''
      }
      return out
    })

    const reload = RELOADS[key]
    if (reload !== undefined && !Array.isArray(next)) {
      const cleared: Partial<TicketCreateAsk> = {}
      if (key === 'ticket_category') cleared.subcategory = 0
      if (key === 'ticket_type') {
        cleared.status = 0
        cleared.category = 0
        cleared.subcategory = 0
      }
      onAsk({ ...ask, ...cleared, [reload]: Number(next) || 0 })
    }
  }

  const isMissing = (f: TicketFormField): boolean =>
    f.type === 'file_input' ? f.required && (files[f.key] ?? []).length === 0 : f.required && isEmpty(values[f.key])

  function submit(): void {
    if (inFlight.current) return
    setRefused(null)
    setTried(true)

    const empty = page.fields.find(isMissing)
    if (empty) {
      toast(`"${empty.label}" is required.`, 'Error', 'error')
      return
    }

    const company = Number(values.ticket_company) || page.company
    const subject = typeof values.subject === 'string' ? values.subject : ''

    /* Restul campurilor pleaca cu numele lor - tot cele din portal - in afara
       de firma si subiect, pe care `createTicket` le pune el. */
    const extra: Record<string, string | string[]> = {}
    for (const field of page.fields) {
      if (field.key === 'ticket_company' || field.key === 'subject' || field.type === 'file_input') continue
      const value = values[field.key]
      if (value === undefined || (typeof value === 'string' && value === '')) continue
      extra[field.key] = value
    }
    /* Felul afectatului pleaca mereu cand formularul are sertarul, ca
       <select name="affected_type"> din portal: "user" pana alege omul altceva. */
    if (page.fields.some((f) => f.type === 'affected_select')) {
      extra.affected_type = typeof values.affected_type === 'string' && values.affected_type !== '' ? values.affected_type : 'user'
    }
    if (!('ticket_type' in extra)) extra.ticket_type = String(page.type)
    if (!('ticket_status' in extra)) extra.ticket_status = String(page.status)
    if (matrix) {
      if (urgency > 0) extra[URGENCY_FIELD] = String(urgency)
      if (impact > 0) extra[IMPACT_FIELD] = String(impact)
      /* Fara drept de suprascriere, prioritatea o hotaraste serverul din matrice. */
      if (computed !== null && !matrix.canOverride) extra.ticket_priority = String(computed)
    }

    /* Fisierele pleaca IN ACEEASI cerere, ca `$_FILES['ticket_attachments']`
       in portal: un camp obligatoriu de fisier se implineste pe loc, iar
       serverul le trimite la descarcari si le lipeste la emailul "created". */
    const pending = Object.values(files).flat()

    /* Pana raspunde serverul butonul se roteste si nu mai primeste clicuri; la
       reusita ramane asa pana se inchide fereastra, la refuz revine. */
    inFlight.current = true
    setSending(true)
    createTicket({ company, subject, extra }, pending)
      .then((answer) => {
        toast('Ticket created.', 'Success', 'success')
        void client.invalidateQueries({ queryKey: ['easeedesk', 'table'] })
        onDone(answer.ticket)
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Error saving'
        setRefused(message)
        toast(message, 'Error', 'error')
        inFlight.current = false
        setSending(false)
      })
  }

  const company = Number(values.ticket_company) || page.company

  return (
    <form
      className="tk-action-form tk-form-create form-horizontal"
      id="createTicketForm"
      onSubmit={(ev) => {
        ev.preventDefault()
        if (!sending) submit()
      }}
    >
      {refused !== null && (
        <div className="form-errors">
          <div className="alert alert-danger" role="alert">
            {refused}
          </div>
        </div>
      )}

      {page.fields.map((field) => {
        if (field.type === 'affected_select') {
          return (
            <AffectedField
              key={field.key}
              field={field}
              company={company}
              kind={(typeof values.affected_type === 'string' && values.affected_type !== '' ? values.affected_type : 'user') as AffectedKind}
              value={values[field.key] ?? ''}
              missing={tried && isMissing(field)}
              onKind={(kind) => setValues((was) => ({ ...was, affected_type: kind, [field.key]: kind === 'user' ? '' : [] }))}
              onChange={(next) => setValues((was) => ({ ...was, [field.key]: next }))}
            />
          )
        }
        if (field.key === 'ticket_priority' && matrix && computed !== null && !matrix.canOverride) {
          /* Prioritatea vine din matrice; lista ar fi o alegere care nu se poate face. */
          return (
            <div className="form-group" key={field.key}>
              <label className="col-sm-3 control-label">{field.label}</label>
              <div className="col-sm-9">
                <p className="form-control-static tk-priority-locked">
                  {priorityName(matrix.priorities, computed)} <small className="text-muted">(from urgency x impact)</small>
                </p>
              </div>
            </div>
          )
        }
        if (field.type === 'file_input') {
          return (
            <FileField
              key={field.key}
              field={field}
              files={files[field.key] ?? []}
              missing={tried && isMissing(field)}
              onChange={(next) => setFiles((was) => ({ ...was, [field.key]: next }))}
            />
          )
        }

        return (
          <Field
            key={field.key}
            id={0}
            field={field}
            value={values[field.key] ?? ''}
            missing={tried && isMissing(field)}
            onChange={(next) => set(field.key, next)}
          />
        )
      })}

      {matrix && (
        <PriorityMatrixFields
          matrix={matrix}
          urgency={urgency}
          impact={impact}
          onUrgency={setUrgency}
          onImpact={setImpact}
          idPrefix="create-ticket"
          disabled={sending}
        />
      )}

      <div className="form-group tk-form-submit">
        <button type="button" className="btn btn-default" onClick={onCancel} disabled={sending}>
          Cancel
        </button>{' '}
        <button type="submit" className="btn btn-primary" name="submit_form" disabled={sending || busy} aria-busy={sending}>
          {sending ? (
            <>
              <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Creating…
            </>
          ) : (
            <>
              <i className="fas fa-check" aria-hidden="true"></i> {page.submit}
            </>
          )}
        </button>
      </div>
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/*  AFECTATUL - felul, si cautarea pe server                                   */
/* -------------------------------------------------------------------------- */

function AffectedField({
  field,
  company,
  kind,
  value,
  missing,
  onKind,
  onChange,
}: {
  field: TicketFormField
  company: number
  kind: AffectedKind
  value: string | string[]
  missing: boolean
  onKind(kind: AffectedKind): void
  onChange(next: string | string[]): void
}) {
  const many = kind !== 'user'
  const [found, setFound] = useState<TicketFormOption[]>([])
  const [searching, setSearching] = useState(false)
  /* Ce s-a ales ramane cu numele lui si dupa ce cautarea a adus altceva. */
  const known = useRef(new Map<string, SelectPickerOption>())
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latest = useRef(0)

  function search(q: string): void {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const stamp = ++latest.current
      setSearching(true)
      searchTicketAffected(company, kind, q)
        .then((answer) => {
          if (stamp !== latest.current) return
          setFound(answer.options)
        })
        .catch(() => {
          if (stamp === latest.current) setFound([])
        })
        .finally(() => {
          if (stamp === latest.current) setSearching(false)
        })
    }, 250)
  }

  /* Firma sau felul s-au schimbat: lista de pana acum nu mai e a lor. */
  useEffect(() => {
    setFound([])
    known.current.clear()
    /* Statiile si elementele de configurare raspund si la gol; contactele cer
       un text (`Owners::searchOwners`). */
    if (kind !== 'user' && company > 0) search('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, kind])

  const chosen = Array.isArray(value) ? value : value === '' ? [] : [value]
  const options: SelectPickerOption[] = found.map((o) => ({ value: String(o.value), label: o.name, subtext: o.subtext }))
  for (const o of options) known.current.set(o.value, o)
  for (const v of chosen) {
    const kept = known.current.get(v)
    if (kept && !options.some((o) => o.value === v)) options.push(kept)
  }

  const kinds = field.options.map((o) => ({ value: String(o.value), label: o.name }))
  const id = `${field.key}-0`

  return (
    <div className={`form-group affected-input-group${missing ? ' has-error' : ''}`}>
      <label htmlFor={id} className="control-label">
        {field.label} {field.required && <span className="required">*</span>}
      </label>
      <div className="input-group">
        <SelectPicker
          id="affected_type-0"
          name="affected_type"
          className="form-control input-sm tk-affected-kind"
          title="Kind"
          size={10}
          options={kinds}
          value={kind}
          onChange={(next) => onKind((next || 'user') as AffectedKind)}
        />
        {many ? (
          <SelectPicker
            id={id}
            name={field.key}
            className="form-control input-sm"
            title={kind === 'equipment' ? 'Type to search devices' : 'Type to search configuration items'}
            size={10}
            multiple
            selectedTextFormat="count > 1"
            options={options}
            value={chosen}
            onChange={onChange}
            onSearch={search}
            searching={searching}
          />
        ) : (
          <SelectPicker
            id={id}
            name={field.key}
            className="form-control input-sm"
            title="Type to search contacts"
            size={10}
            options={options}
            value={chosen[0] ?? ''}
            onChange={onChange}
            onSearch={search}
            searching={searching}
          />
        )}
      </div>
      {missing && <span className="help-block tk-field-missing">{field.label} is required.</span>}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  FISIERELE - pleaca dupa ce tichetul exista                                 */
/* -------------------------------------------------------------------------- */

function FileField({ field, files, missing, onChange }: { field: TicketFormField; files: File[]; missing: boolean; onChange(next: File[]): void }) {
  const id = `${field.key}-0`

  return (
    <div className={missing ? 'form-group has-error' : 'form-group'}>
      <label htmlFor={id} className="control-label">
        {field.label} {field.required && <span className="required">*</span>}
      </label>
      <input
        id={id}
        name={`${field.key}[]`}
        type="file"
        multiple
        className="form-control input-sm"
        onChange={(ev) => onChange([...(ev.target.files ?? [])])}
      />
      {files.length > 0 && (
        <p className="help-block">
          {files.length} file{files.length === 1 ? '' : 's'} selected.
        </p>
      )}
      {missing && <span className="help-block tk-field-missing">{field.label} is required.</span>}
    </div>
  )
}
